import * as React from 'react'
import { Link, Typography, Accordion } from '@lacedltd/laced-components'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'
import { useTranslation } from 'hooks'
import { authIcon, brandNewIcon, deliveryIcon, returnsIcon } from 'images'
import * as S from './ProductUspList.styles'

dayjs.extend(utc)
dayjs.extend(tz)

const ProductUspList: React.FC<{ premiumShippable?: boolean }> = ({
  premiumShippable = false
}) => {
  const { t } = useTranslation()
  const scopedT: typeof t = (key, opts) =>
    t(`react_components.product.${key}`, opts)
  const fivePM = useLocalisedBritishTime(17, 0, 0)
  const premiumShipTitle = 'premium_delivery'
  const accordionItems = [
    {
      title: premiumShipTitle,
      description: (
        <>
          <S.ProductUspDescriptionParagraph
            variant="body"
            as="p"
            color="airJordan.p"
          >
            {scopedT('premium_delivery_desc.available_with_next_day_delivery')}
          </S.ProductUspDescriptionParagraph>
          <S.PremiumDeliveryList variant="body" as="ul" color="airJordan.p">
            <li>{scopedT('premium_delivery_desc.authenticity_guaranteed')}</li>
            <li>
              {scopedT('premium_delivery_desc.our_fastest_delivery_option')}
            </li>
            <li>
              {scopedT('premium_delivery_desc.order_deadline', {
                time: fivePM
              })}
            </li>
          </S.PremiumDeliveryList>
          <S.PremiumShipTerms variant="caption" color="airJordan.p" as="p">
            {scopedT('premium_delivery_desc.terms_apply')}{' '}
            <Link
              href={`${t('shared.help_url')}${t(
                'shared.help_premium_delivery_path'
              )}`}
              size="s"
              underline
            >
              {scopedT('read_more')}
            </Link>
          </S.PremiumShipTerms>
        </>
      ),
      icon: <IconNode src={deliveryIcon} alt="Premium" />,
      hidden: !premiumShippable
    },
    {
      title: 'authenticated',
      description: t('react_components.product.authenticated_desc'),
      icon: <IconNode src={authIcon} alt="Authentication" />
    },
    {
      title: 'brand_new_unused',
      description: t('react_components.product.brand_new_unused_desc'),
      icon: <IconNode src={brandNewIcon} alt="Brand new" />
    },
    {
      title: 'returns_accepted',
      description: (
        <>
          <S.ProductUspDescriptionParagraph
            variant="body"
            as="p"
            color="airJordan.p"
          >
            {scopedT('returns_accepted_desc')}
          </S.ProductUspDescriptionParagraph>
          <Link
            href={`${t('shared.help_url')}${t('shared.help_returns_path')}`}
            size="m"
            underline
          >
            {scopedT('read_more')}
          </Link>
        </>
      ),
      icon: <IconNode src={returnsIcon} alt="Returns accepted" />
    }
  ]

  return (
    <S.ProductUspList>
      <S.StyledAccordionRoot type="single" defaultValue={premiumShipTitle}>
        {accordionItems.map(({ icon, title, description, hidden }) => {
          if (hidden) return null
          return (
            <Accordion.Item
              key={title}
              title={
                <S.AccordionTriggerImageWrapper>
                  {icon}
                  <S.ProductUspTitle as="h5">
                    {scopedT(title)}
                  </S.ProductUspTitle>
                </S.AccordionTriggerImageWrapper>
              }
              sectionName={title}
            >
              <S.ProductUspTextWrapper>
                <Typography.Text variant="body" color="airJordan.p">
                  {description}
                </Typography.Text>
              </S.ProductUspTextWrapper>
            </Accordion.Item>
          )
        })}
      </S.StyledAccordionRoot>
    </S.ProductUspList>
  )
}

export default ProductUspList

const IconNode: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <S.ProductUspIcon src={src} alt={alt} />
)

const useLocalisedBritishTime = (hours: number, min: number, sec: number) => {
  return React.useMemo(() => {
    const viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const britishTime = dayjs
      .tz(new Date(), 'Europe/London')
      .set('h', hours)
      .set('minute', min)
      .set('second', sec)

    return britishTime.tz(viewerTimeZone).format('ha')
  }, [hours, min, sec])
}
